// Typed fetch wrapper for the HTTP API. Attaches the Cognito ID token and
// surfaces the server error envelope as ApiError. A 401 clears the session
// so the route guard can send the user back to #/login.

import { getIdToken, signOut } from "./auth.js";

const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, "");

export function apiBaseUrl(): string {
  return BASE ?? "";
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly requestId?: string,
    readonly fieldErrors?: Record<string, string>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function clearSession(): Promise<void> {
  try {
    await signOut();
  } catch {
    /* already signed out */
  }
  onUnauthorized?.();
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  if (!BASE) throw new ApiError("VITE_API_URL is not configured.", 0, "CONFIG");

  const headers = new Headers(init.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");

  const token = await getIdToken();
  if (!token) {
    await clearSession();
    throw new ApiError("Not signed in.", 401, "UNAUTHORIZED");
  }
  headers.set("Authorization", `Bearer ${token}`);

  let body = init.body;
  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.json);
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers, body });
  } catch {
    throw new ApiError("You're offline. Try again when you're back online.", 0, "OFFLINE");
  }

  if (res.status === 401) {
    await clearSession();
    throw new ApiError("Session expired. Please sign in again.", 401, "UNAUTHORIZED");
  }

  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const envelope = parsed as {
      error?: { code?: string; message?: string; requestId?: string };
      errors?: Record<string, string>;
    } | null;
    const err = envelope?.error;
    throw new ApiError(
      err?.message ?? res.statusText,
      res.status,
      err?.code,
      err?.requestId,
      envelope?.errors,
    );
  }

  return parsed as T;
}

export function isOfflineError(err: unknown): boolean {
  return err instanceof ApiError && err.code === "OFFLINE";
}

export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

/**
 * Uploads a blob directly to a presigned S3 PUT URL. Never goes through the
 * API Gateway: reference-asset and export payloads bypass the Lambda body
 * limit entirely (D-2 / worker upload pattern reused by the SPA).
 */
export async function apiUpload(presignedUrl: string, blob: Blob, mimeType: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(presignedUrl, {
      method: "PUT",
      headers: { "Content-Type": mimeType },
      body: blob,
    });
  } catch {
    throw new ApiError("Upload failed. Try again when you're back online.", 0, "OFFLINE");
  }
  if (!res.ok) {
    throw new ApiError(`Upload failed (${res.status})`, res.status);
  }
}
